import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, X } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency, formatDateTime, downloadCSV } from "../../lib/format";

const STATUS_TONES = {
    confirmed: "bg-teal-50 text-teal-700",
    cancelled: "bg-red-50 text-red-700",
    completed: "bg-navy-50 text-navy-600",
    pending: "bg-saffron-50 text-saffron-700",
};

const ManageBookings = () => {
    const { bookings, updateBooking } = useData();
    const [type, setType] = useState("all");
    const [status, setStatus] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [view, setView] = useState(null);

    const filtered = useMemo(() => {
        return bookings.filter((b) => {
            if (type !== "all" && b.type !== type) return false;
            if (status !== "all" && b.status !== status) return false;
            const created = b.createdAt?.slice(0, 10);
            if (startDate && created < startDate) return false;
            if (endDate && created > endDate) return false;
            return true;
        });
    }, [bookings, type, status, startDate, endDate]);

    const setStatusFor = (id, newStatus) => {
        updateBooking(id, { status: newStatus });
        toast.success(`Booking marked as ${newStatus}.`);
    };

    const exportCSV = () => {
        const rows = filtered.map((b) => ({
            id: b.id,
            type: b.type,
            customer: b.customer.name,
            email: b.customer.email,
            phone: b.customer.phone,
            item: b.cabName || b.packageName,
            from: b.from || b.startingCity || "",
            to: b.to || "",
            date: b.date || b.startDate || "",
            amount: b.totalAmount,
            status: b.status,
            createdAt: b.createdAt,
        }));
        if (rows.length === 0) return toast.error("No bookings to export.");
        downloadCSV(`southtrip-bookings-${Date.now()}.csv`, rows);
    };

    return (
        <div data-testid="admin-bookings-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl text-ink">Bookings</h1>
                    <p className="text-ink-light text-sm mt-1">{filtered.length} of {bookings.length} bookings</p>
                </div>
                <button onClick={exportCSV} className="px-4 py-2.5 rounded-xl border border-slate-200 hover:border-saffron-300 text-ink font-semibold flex items-center gap-2 text-sm" data-testid="admin-export-csv">
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>

            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-light font-semibold">Type</label>
                    <select value={type} onChange={(e) => setType(e.target.value)} className="bare mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
                        <option value="all">All</option>
                        <option value="cab">Cab</option>
                        <option value="package">Package</option>
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-light font-semibold">Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="bare mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
                        <option value="all">All</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-light font-semibold">From date</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-light font-semibold">To date</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" />
                </div>
            </div>

            <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs uppercase tracking-wider text-ink-light">
                                <th className="px-4 py-3 font-semibold">ID</th>
                                <th className="px-4 py-3 font-semibold">Type</th>
                                <th className="px-4 py-3 font-semibold">Customer</th>
                                <th className="px-4 py-3 font-semibold">Item</th>
                                <th className="px-4 py-3 font-semibold">Amount</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3 font-semibold">Created</th>
                                <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-ink-light py-10">No bookings.</td></tr>
                            )}
                            {filtered.map((b) => (
                                <tr key={b.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-mono text-xs text-ink">{b.id}</td>
                                    <td className="px-4 py-3 capitalize">{b.type}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-ink">{b.customer.name}</div>
                                        <div className="text-xs text-ink-light">{b.customer.email}</div>
                                    </td>
                                    <td className="px-4 py-3 text-ink-light line-clamp-1 max-w-xs">{b.cabName || b.packageName}</td>
                                    <td className="px-4 py-3 font-semibold text-saffron-600">{formatCurrency(b.totalAmount)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_TONES[b.status] || STATUS_TONES.pending}`}>{b.status}</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-ink-light">{formatDateTime(b.createdAt)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => setView(b)} className="p-2 rounded-md hover:bg-slate-100 text-ink-light" title="View"><Eye className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {view && (
                <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={() => setView(null)}>
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-2xl text-ink">Booking detail</h2>
                            <button onClick={() => setView(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <Info label="Booking ID" v={view.id} />
                            <Info label="Type" v={view.type} />
                            <Info label="Customer" v={view.customer.name} />
                            <Info label="Email" v={view.customer.email} />
                            <Info label="Phone" v={view.customer.phone} />
                            <Info label="Item" v={view.cabName || view.packageName} />
                            <Info label="Route" v={view.type === "cab" ? `${view.from} → ${view.to}` : `From ${view.startingCity}`} />
                            <Info label="Date" v={view.date || view.startDate} />
                            <Info label="Amount" v={formatCurrency(view.totalAmount)} />
                            <Info label="Payment" v={(view.payment || "").toUpperCase()} />
                            <Info label="Status" v={view.status} />
                            <Info label="Created" v={formatDateTime(view.createdAt)} />
                        </div>
                        {view.customer.notes && (
                            <div className="mt-4">
                                <div className="text-xs uppercase tracking-wider text-ink-light font-semibold">Customer notes</div>
                                <div className="text-sm text-ink mt-1">{view.customer.notes}</div>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-200">
                            <button onClick={() => { setStatusFor(view.id, "confirmed"); setView({ ...view, status: "confirmed" }); }} className="px-4 py-2 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-xs font-semibold">Confirm</button>
                            <button onClick={() => { setStatusFor(view.id, "completed"); setView({ ...view, status: "completed" }); }} className="px-4 py-2 rounded-lg bg-navy-600 hover:bg-navy-700 text-white text-xs font-semibold">Mark completed</button>
                            <button onClick={() => { setStatusFor(view.id, "cancelled"); setView({ ...view, status: "cancelled" }); }} className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Info = ({ label, v }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-light font-semibold">{label}</div>
        <div className="text-ink font-medium mt-0.5 break-words">{v || "—"}</div>
    </div>
);

export default ManageBookings;
